import type { CSSProperties } from 'react';

export const ANALYTICS_EXPORT_PAGE_WIDTH_MM = 297;
export const ANALYTICS_EXPORT_PAGE_HEIGHT_MM = 210;

/**
 * The screenshot service sets its own Puppeteer margins and ignores
 * `@page { margin: 0 }`, so a full-bleed background always printed framed in
 * white. The page is white instead and the colour lives in the tiles, which
 * makes whatever margin the service applies invisible.
 */
export const ANALYTICS_EXPORT_PAGE_PADDING_MM = 10;

/**
 * When printing, the initial containing block is the page content box, so
 * `100vh` is exactly the printable height whatever margins the screenshot
 * service applies. On screen it is the viewport.
 *
 * It has to be a definite height, not a minimum: the chart canvas is sized as
 * a percentage of its container, so a container that grows with its content
 * closes a loop and chart.js inflates the page on every resize.
 */
export const ANALYTICS_EXPORT_PAGE_HEIGHT = '100vh';

export const ANALYTICS_EXPORT_ROOT_CLASS_NAME = 'analytics-export-pdf';

/** The chart fills its card, which in turn fills the page body. */
export const ANALYTICS_EXPORT_CHART_HEIGHT = '100%';

export const ANALYTICS_EXPORT_METRIC_GRID_GAP_PX = 16;

/**
 * Keeps rows even: four tiles read better as 2x2 than as 3+1, and anything
 * above six needs three columns to stay within the page height.
 */
export function resolveAnalyticsExportColumns(tileCount: number): number {
  if (tileCount <= 3) {
    return Math.max(tileCount, 1);
  }

  return tileCount === 4 ? 2 : 3;
}

export function getAnalyticsExportTileStyle(columns: number): CSSProperties {
  const gapShare =
    (ANALYTICS_EXPORT_METRIC_GRID_GAP_PX * (columns - 1)) / columns;
  const basis = `calc(${100 / columns}% - ${gapShare}px)`;

  return { flex: `1 1 ${basis}`, minWidth: basis };
}

export const analyticsExportPageShellClassName =
  'analytics-export-page tw-box-border tw-flex tw-w-full tw-flex-col';

export function getAnalyticsExportPageStyle(
  pageNumber: number,
  totalPages: number,
): CSSProperties {
  return {
    boxSizing: 'border-box',
    // Width follows the printable box rather than the sheet, so the service's
    // own margins cannot push the page onto an extra sheet.
    width: '100%',
    height: ANALYTICS_EXPORT_PAGE_HEIGHT,
    overflow: 'hidden',
    padding: `${ANALYTICS_EXPORT_PAGE_PADDING_MM}mm`,
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
    breakBefore: pageNumber > 1 ? 'page' : 'auto',
    pageBreakBefore: pageNumber > 1 ? 'always' : 'auto',
    breakAfter: pageNumber < totalPages ? 'page' : 'auto',
    pageBreakAfter: pageNumber < totalPages ? 'always' : 'auto',
  };
}
